import { cn } from '@lib/utils';
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';

export const ExpandableText: FunctionComponent<{
  className?: string;
  maxLines?: 1 | 2 | 3 | 4 | 5;
  text: ReactNode;
}> = ({ className, maxLines = 2, text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [canOverflow, setCanOverflow] = useState(false); // Tracks if text *can* overflow
  const containerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: This is expected
  useEffect(() => {
    let rafId: number;
    const checkOverflow = () => {
      cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        if (!containerRef.current) return;

        const element = containerRef.current;

        // Compare scrollHeight (full content height) with clientHeight (visible height)
        // Add 2px threshold to account for rounding/rendering differences
        const overflows = element.scrollHeight > element.clientHeight + 2;

        setCanOverflow(overflows);
      });
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [text, maxLines]);

  return (
    <div className={cn('relative', className)}>
      <div
        ref={containerRef}
        className={cn(
          'overflow-hidden whitespace-pre-wrap',
          // This looks a bit ugly, but we can't set a dynamic line-clamp class using Tailwind...
          // so for now we limit the options of maxLines to 5
          !isExpanded && maxLines === 1 && 'line-clamp-1',
          !isExpanded && maxLines === 2 && 'line-clamp-2',
          !isExpanded && maxLines === 3 && 'line-clamp-3',
          !isExpanded && maxLines === 4 && 'line-clamp-4',
          !isExpanded && maxLines === 5 && 'line-clamp-5',
          !isExpanded && canOverflow && 'pr-10',
        )}
      >
        {text}
      </div>

      {canOverflow && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            'font-medium text-gray-500 hover:text-gray-700',
            isExpanded ? 'inline-block mt-1' : 'absolute bottom-0 right-0 pb-px pr-1',
          )}
        >
          {isExpanded ? 'less' : 'more'}
        </button>
      )}
    </div>
  );
};
